import {compact} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import EditPrimerTabState from 'analyses/EditPrimerTabState';
import {ExistingPrimers} from 'analyses/ExistingPrimersTemplate';
import BenchlingConfig from 'BenchlingConfig';
import * as biojs from 'BioJS';
import {SequencePrimer} from 'BioJS';
import {sanitize} from 'biojs/Util';
import {MinimalFolder} from 'constants/FolderConstants';
import {OLIGO_NUCLEOTIDE_TYPE_TO_SEQUENCE_SUBTYPE} from 'constants/ModelTypeConstants';
import FolderSelector from 'file-browser/FolderSelector';
import MultipleFolderSelector from 'file-browser/MultipleFolderSelector';
import staticURL from 'lib/staticURL';
import {FindPrimersOptions} from 'molbio/find-primers/FindPrimersConstants';
import FindPrimersResults from 'molbio/find-primers/FindPrimersResults';
import {FindPrimersTabState} from 'molbio/find-primers/FindPrimersTabState';
import NucleotideTypeSelector from 'molbio/find-primers/NucleotideTypeSelector';
import MeltingTempModal from 'molbio/melting-temp/MeltingTempModal';
import MisprimeParams from 'molbio/misprime/MisprimeParams';
import {getBaseCoercionWarningText} from 'molbio/MolBioUtil';
import PopoverColorPicker from 'molbio/sequence-features/PopoverColorPicker';
import * as sequtil from 'molbio/SeqUtil';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import {FONT_PROXIMA_11} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import NumericInput from 'ui-library/inputs/NumericInput';
import TextField from 'ui-library/inputs/TextField';
import TextInput from 'ui-library/inputs/TextInput';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import {withLoadingScreen} from 'util/Promises';
import {GREATER_OR_EQUAL_SIGN} from 'util/UnicodeSymbols';
import {WorklistType} from 'worklists/WorklistConstants';
import WorklistSelector from 'worklists/WorklistSelector';

function getPrimerLibraryInfo(primer: SequencePrimer): string {
  if (primer.folder() != null) {
    return ` (${primer.get('owner')} / ${primer.folder().name})`;
  } else {
    return '';
  }
}

interface SaveBasesAsOligoFormProps {
  color: string;
  setColor: (color: string) => void;
  createOligoAndSavePrimers: () => Promise<void>;
  folderId: string | null;
  setFolderId: (folderId: string) => void;
  name: string;
  setName: (name: string) => void;
  saveDisabled: boolean;
}

/**
 * Form to save bases as a new oligo and bind it as a primer to the chosen match sites.
 *
 * Searching with raw bases (FindPrimersOptions.BASES) is a special case where it
 * does not correspond to an existing oligo. To create it, the user needs to pick
 * a name and folder for the new entity.
 */
export function SaveBasesAsOligoForm({
  name,
  setName,
  color,
  setColor,
  createOligoAndSavePrimers,
  folderId,
  setFolderId,
  saveDisabled,
}: SaveBasesAsOligoFormProps): JSX.Element {
  return (
    <>
      <Row>
        <Col md={3} />
        <Col md={6}>
          <TextField
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
            name="primer-name"
            label="Name"
            placeholder="Enter a short name"
            dataTestAttrs={{component: 'FindPrimersTab', element: 'newPrimerNameField'}}
          />
        </Col>
        <Col md={1}>
          <FormGroup>
            <FieldLabel label="Color" />
            <PopoverColorPicker selectedColor={color} onSelectColor={setColor} />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={3} />
        <Col md={6}>
          <FormGroup>
            <FieldLabel label="Folder" />
            <FolderSelector
              value={folderId}
              onChange={(e) => {
                setFolderId(e.target.value);
              }}
              showInventory={true}
              enableRedesign={true}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={3} />
        <Col md={6}>
          <Button
            buttonStyle={ButtonStyle.PRIMARY}
            size={ButtonSize.SMALL}
            disabled={saveDisabled || name.length === 0 || folderId == null}
            onClick={async () => {
              await withLoadingScreen(createOligoAndSavePrimers());
            }}
            dataTestAttrs={{component: 'FindPrimersTab', element: 'saveSelectedPrimersButton'}}
          >
            Save Selected Primers
          </Button>
        </Col>
      </Row>
    </>
  );
}

interface PrimerPositionInputsProps {
  setFromSelectionEnabled: boolean;
  onSetFromSelection: () => void;
  sequenceStartIndex: number;
  sequenceBases: string;
  minBaseIndex: number;
  setMinBaseIndex: (value: number) => void;
  maxBaseIndex: number;
  setMaxBaseIndex: (value: number) => void;
}

/**
 * Form to specify where on the sequence to search for primer binding sites.
 *
 * Users can narrow a larger sequence to an area of interest when searching for primers.
 */
export function PrimerPositionInputs(props: PrimerPositionInputsProps): JSX.Element {
  return (
    <>
      <div className={css(styles.positionText)}>
        with 3' position in{' '}
        <NumericInput
          className={css(styles.positionInput)}
          value={sequtil.modelStartToDisplay(props.minBaseIndex, props.sequenceStartIndex)}
          placeholder={sequtil.modelStartToDisplay(0, props.sequenceStartIndex).toString()}
          onChange={(e) => {
            if (e.target.numericValue == null) {
              props.setMinBaseIndex(0);
            } else {
              props.setMinBaseIndex(
                sequtil.displayStartToModel(e.target.numericValue, props.sequenceStartIndex)
              );
            }
          }}
        />
        {' - '}
        <NumericInput
          className={css(styles.positionInput)}
          value={sequtil.modelEndToDisplay(
            props.maxBaseIndex,
            props.sequenceBases.length,
            props.sequenceStartIndex
          )}
          placeholder={sequtil
            .modelEndToDisplay(0, props.sequenceBases.length, props.sequenceStartIndex)
            .toString()}
          onChange={(e) => {
            if (e.target.numericValue == null) {
              props.setMaxBaseIndex(0);
            } else {
              props.setMaxBaseIndex(
                sequtil.displayEndToModel(
                  e.target.numericValue,
                  props.sequenceBases.length,
                  props.sequenceStartIndex
                )
              );
            }
          }}
        />
      </div>
      <div className={css(styles.useSelectionButton)}>
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.MINI}
          disabled={!props.setFromSelectionEnabled}
          onClick={props.onSetFromSelection}
        >
          Use selection
        </Button>
      </div>
    </>
  );
}

const FIND_TYPE_TO_DESCRIPTION: {[findType in FindPrimersOptions]: string} = {
  [FindPrimersOptions.FOLDERS]: 'for all oligos in specified folders',
  [FindPrimersOptions.SINGLE]: 'for a single saved oligo',
  [FindPrimersOptions.BASES]: 'for arbitrary bases you provide',
  [FindPrimersOptions.WORKLIST]: 'for all oligos in specified worklist',
  [FindPrimersOptions.TAG_SCHEMA]: 'for all oligos with a schema',
};

/**
 * Inputs for selecting which find type to perform when searching for primers.
 */
function FindTypeForm({
  findPrimersTabState,
  searchOligos,
  onFindPrimers,
}: {
  findPrimersTabState: FindPrimersTabState;
  searchOligos: () => Promise<void>;
  onFindPrimers: () => Promise<void>;
}): JSX.Element {
  const [hasSanitizedBases, setHasSanitizedBases] = useState(false);
  const [hasSearchedOligos, setHasSearchedOligos] = useState(false);
  const findType = findPrimersTabState.get('findType');
  const nucleotideType = findPrimersTabState.get('nucleotideType');
  useEffect(() => {
    setHasSanitizedBases(false);
    setHasSearchedOligos(false);
  }, [findType]);
  useEffect(() => {
    setHasSanitizedBases(false);
  }, [nucleotideType]);

  return (
    <>
      <Row>
        <Col sm={3} className="text-right">
          Find binding sites
        </Col>
        <Col sm={6}>
          <SelectDropdown
            dataTestAttrs={{component: 'FindPrimersTabContent', element: 'findTypeSelectDropdown'}}
            menu={
              <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FIT_CONTENT} value={findType}>
                {compact([
                  FindPrimersOptions.FOLDERS,
                  FindPrimersOptions.SINGLE,
                  FindPrimersOptions.BASES,
                  BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS ? FindPrimersOptions.WORKLIST : undefined,
                  FindPrimersOptions.TAG_SCHEMA,
                ]).map((findTypeOption) => (
                  <Option
                    dataTestAttrs={{
                      component: 'FindPrimersTabContent',
                      element: 'findTypeMenuOption',
                      key: findTypeOption.toString(),
                    }}
                    key={findTypeOption}
                    value={findTypeOption}
                    onSelect={() => findPrimersTabState.set('findType', findTypeOption)}
                  >
                    {FIND_TYPE_TO_DESCRIPTION[findTypeOption]}
                  </Option>
                ))}
              </Menu>
            }
            displayName={FIND_TYPE_TO_DESCRIPTION[findType]}
          />
        </Col>
      </Row>
      <hr />
      {findType === FindPrimersOptions.FOLDERS && (
        <>
          <Row>
            <Col sm={3} className="text-right u-small-padding-top">
              Use oligos in
            </Col>
            <Col sm={9}>
              <MultipleFolderSelector
                selectedFolderIds={findPrimersTabState.get('selectedLibraries')}
                showInventory={true}
                onSelectFolders={(selectedFolders: Array<MinimalFolder>) => {
                  findPrimersTabState.set(
                    'selectedLibraries',
                    selectedFolders.map((folder) => folder.api_identifier)
                  );
                }}
                dataTestAttrs={{component: 'FindPrimersTab', element: 'folderSelector'}}
              />
            </Col>
          </Row>
          <Row>
            <Col sm={3} />
            <Col sm={9}>
              <NucleotideTypeSelector
                nucleotideType={nucleotideType}
                setNucleotideType={(nucleotideTypeOption) =>
                  findPrimersTabState.set('nucleotideType', nucleotideTypeOption)
                }
              />
            </Col>
          </Row>
        </>
      )}
      {findType === FindPrimersOptions.SINGLE && (
        <>
          <Row>
            <Col md={3} className="left-label text-right">
              Search folders for
            </Col>
            <Col md={6}>
              <TextInput
                className="search-name search-input"
                value={findPrimersTabState.get('searchName')}
                onChange={(e) => {
                  findPrimersTabState.set('searchName', e.target.value);
                }}
                placeholder="Name"
                dataTestAttrs={{
                  component: 'SavedPrimerFind',
                  element: 'primerName',
                }}
              />
            </Col>
          </Row>
          <Row>
            <Col md={3} />
            <Col md={6}>
              <NucleotideTypeSelector
                nucleotideType={nucleotideType}
                setNucleotideType={(nucleotideTypeOption) =>
                  findPrimersTabState.set('nucleotideType', nucleotideTypeOption)
                }
              />
            </Col>
          </Row>
          <Row>
            <Col md={6} md-offset={3}>
              <TextInput
                className="search-bases search-input"
                value={findPrimersTabState.get('searchBases')}
                onChange={(e) => {
                  const rawInput = e.target.value;
                  const sanitizedBases = sanitize(rawInput, nucleotideType);
                  setHasSanitizedBases(rawInput !== sanitizedBases);
                  findPrimersTabState.set('searchBases', sanitizedBases);
                  findPrimersTabState.set('minMatchingBases', sanitizedBases.length);
                }}
                placeholder="Bases (exact)"
              />
              {hasSanitizedBases && (
                <div className={css(styles.baseCoercionWarning)}>
                  {getBaseCoercionWarningText(nucleotideType)}
                </div>
              )}
            </Col>
            <Col md={3}>
              <Button
                buttonStyle={ButtonStyle.PRIMARY}
                size={ButtonSize.SMALL}
                dataTestAttrs={{component: 'SavedPrimerFind', element: 'primerSearchButton'}}
                disabled={
                  findPrimersTabState.get('searchBases') === '' &&
                  findPrimersTabState.get('searchName') === ''
                }
                onClick={async () => {
                  await withLoadingScreen(searchOligos());
                  setHasSearchedOligos(true);
                }}
              >
                Search
              </Button>
            </Col>
          </Row>
          {(hasSearchedOligos || findPrimersTabState.get('searchExisting')!.length > 0) && (
            <Row>
              <Col md={9} md-offset={3}>
                <ul className="search-results scroll">
                  {findPrimersTabState.get('searchExisting')!.models.map((sequencePrimer) => (
                    <li
                      key={sequencePrimer.cid}
                      className={sequencePrimer.get('selected') ? 'selected' : ''}
                      onClick={() => {
                        for (const otherPrimer of findPrimersTabState.get('searchExisting')!.models) {
                          otherPrimer.set({selected: false});
                        }
                        sequencePrimer.set({selected: true});
                        withLoadingScreen(onFindPrimers());
                      }}
                    >
                      <div
                        className="result-header"
                        {...getTestProps({
                          component: 'PrimerSearchResult',
                          element: 'resultHeader',
                          key: sequencePrimer.get('name'),
                        })}
                        data-amp-safe={false}
                      >
                        {sequencePrimer.get('name')}
                        {getPrimerLibraryInfo(sequencePrimer)}
                      </div>
                      <div className="bases mono">{sequencePrimer.get('bases')}</div>
                    </li>
                  ))}
                </ul>
                {findPrimersTabState.get('searchExisting')!.length === 0 && (
                  <p>No matching primers were found.</p>
                )}
              </Col>
            </Row>
          )}
        </>
      )}
      {findType === FindPrimersOptions.BASES && (
        <>
          <Row>
            <Col sm={3} />
            <Col sm={6}>
              <TextInput
                value={findPrimersTabState.get('searchBases')}
                onChange={(e) => {
                  const rawInput = e.target.value;
                  const sanitizedBases = sanitize(rawInput, nucleotideType);
                  setHasSanitizedBases(rawInput !== sanitizedBases);
                  findPrimersTabState.set('searchBases', sanitizedBases);
                  findPrimersTabState.set('minMatchingBases', sanitizedBases.length);
                }}
                placeholder="Bases"
                dataTestAttrs={{component: 'BasesSearchInput', element: 'basesSearchInput'}}
              />
              {hasSanitizedBases && (
                <div className={css(styles.baseCoercionWarning)}>
                  {getBaseCoercionWarningText(nucleotideType)}
                </div>
              )}
            </Col>
          </Row>
          <Row>
            <Col sm={3} />
            <Col sm={6}>
              <NucleotideTypeSelector
                nucleotideType={nucleotideType}
                setNucleotideType={(nucleotideTypeOption) =>
                  findPrimersTabState.set('nucleotideType', nucleotideTypeOption)
                }
              />
            </Col>
          </Row>
        </>
      )}
      {findType === FindPrimersOptions.WORKLIST && (
        <>
          <Row>
            <Col sm={3} className="text-right u-small-padding-top">
              Use oligos in
            </Col>
            <Col sm={9}>
              <WorklistSelector
                onChange={(worklistDetails) => {
                  if (worklistDetails.worklist != null) {
                    findPrimersTabState.set('selectedLibraries', [worklistDetails.worklist.id]);
                  } else {
                    findPrimersTabState.set('selectedLibraries', []);
                  }
                }}
                worklistTypes={[WorklistType.BIOENTITY]}
                label=""
              />
            </Col>
          </Row>
          <Row>
            <Col sm={3} />
            <Col sm={9}>
              <NucleotideTypeSelector
                nucleotideType={nucleotideType}
                setNucleotideType={(nucleotideTypeOption) =>
                  findPrimersTabState.set('nucleotideType', nucleotideTypeOption)
                }
              />
            </Col>
          </Row>
        </>
      )}
      {findType === FindPrimersOptions.TAG_SCHEMA && (
        <Row>
          <Col sm={3} className="left-label text-right">
            With schema
          </Col>
          <Col sm={6}>
            <TagSchemaSelector
              allowNone={false}
              oligoAwareFolderItemTypes={[
                OLIGO_NUCLEOTIDE_TYPE_TO_SEQUENCE_SUBTYPE[findPrimersTabState.get('nucleotideType')],
              ]}
              onChange={(tagSchema) => {
                findPrimersTabState.set('tagSchemaId', tagSchema ? tagSchema.id : null);
              }}
              selectedTagSchemaId={findPrimersTabState.get('tagSchemaId')}
              forceVisibleSelectedTagSchema={null}
            />
          </Col>
        </Row>
      )}
    </>
  );
}

interface FindPrimersTabProps {
  createOligoAndSavePrimers: () => Promise<void>;
  onFindPrimers: () => Promise<void>;
  findPrimersTabState: FindPrimersTabState;
  searchOligos: () => Promise<void>;
}

export default function FindPrimersTab({
  findPrimersTabState,
  onFindPrimers,
  createOligoAndSavePrimers,
  searchOligos,
}: FindPrimersTabProps): JSX.Element {
  const sequence = findPrimersTabState.get('sequence');
  const sequenceStartIndex = sequence.startIndex();
  const sequenceBases = sequence.bases();
  const findType = findPrimersTabState.get('findType');

  const hasMinMatchingBases = findPrimersTabState.get('minMatchingBases')! >= biojs.MIN_PRIMER_LENGTH;
  return (
    <>
      <FindTypeForm
        findPrimersTabState={findPrimersTabState}
        searchOligos={searchOligos}
        onFindPrimers={onFindPrimers}
      />
      {(findType !== FindPrimersOptions.SINGLE || findPrimersTabState.get('searchExisting')!.length > 0) && (
        <>
          <Row>
            <Col sm={3} className="text-right">
              {!hasMinMatchingBases && (
                <strong className="control-label text-danger">
                  Must match {`${GREATER_OR_EQUAL_SIGN} ${biojs.MIN_PRIMER_LENGTH}`}.
                </strong>
              )}
            </Col>
            <Col sm={9}>
              <MisprimeParams
                misprimeParams={findPrimersTabState.getMisprimingState()}
                setMisprimeParams={(misprimeParams) => findPrimersTabState.set(misprimeParams)}
                showExactAndUniqueMatch={true}
              />
            </Col>
          </Row>
          <Row>
            <Col sm={3} />
            <Col sm={9}>
              <PrimerPositionInputs
                setFromSelectionEnabled={sequence.selection().isNotEmpty()}
                onSetFromSelection={() => {
                  const selection = sequence.selection().assertNotEmpty();
                  findPrimersTabState.set({
                    minBaseIndex: selection.start(),
                    maxBaseIndex: selection.end(),
                  });
                }}
                sequenceStartIndex={sequenceStartIndex}
                sequenceBases={sequenceBases}
                minBaseIndex={findPrimersTabState.get('minBaseIndex')}
                setMinBaseIndex={(value) => findPrimersTabState.set('minBaseIndex', value)}
                maxBaseIndex={findPrimersTabState.get('maxBaseIndex')}
                setMaxBaseIndex={(value) => findPrimersTabState.set('maxBaseIndex', value)}
              />
            </Col>
          </Row>
          <Row>
            <Col sm={3} />
            <Col sm={9}>
              <Button
                buttonStyle={ButtonStyle.SECONDARY}
                className="u-mini-spacing-right"
                size={ButtonSize.SMALL}
                dataTestAttrs={{component: 'FindPrimersTab', element: 'findBindingSitesButton'}}
                disabled={findPrimersTabState.findPrimersDisabled() || !hasMinMatchingBases}
                onClick={async () => {
                  await withLoadingScreen(onFindPrimers());
                }}
              >
                Find binding sites
              </Button>
              <Button
                buttonStyle={ButtonStyle.LINK}
                onClick={() => {
                  legacyRunModal(MeltingTempModal, {});
                }}
              >
                T<sub>m</sub> parameters
              </Button>
            </Col>
          </Row>
          <Row>
            <Col sm={9} sm-offset={3}>
              {findType === FindPrimersOptions.BASES &&
                findPrimersTabState.get('searchExistingBases')!.length > 0 && (
                  <ExistingPrimers
                    primers={findPrimersTabState.get('searchExistingBases')!.models}
                    single={true}
                    used={false}
                    isAssemblyPrimer={false}
                    onSelectPrimer={(primer) => {
                      primer.set({selected: true});
                      findPrimersTabState.get('searchExisting')!.reset([primer]);
                      findPrimersTabState.set('findType', FindPrimersOptions.SINGLE);
                    }}
                  />
                )}
            </Col>
          </Row>
          <hr />
          {findPrimersTabState.get('showResults') && (
            <>
              <p>
                {findPrimersTabState.get('moreResults') ? (
                  <span>
                    <strong>Too many matches were found</strong> &mdash;only the first{' '}
                    {findPrimersTabState.get('maxResults')} matches are shown here.
                  </span>
                ) : (
                  <span>
                    <strong>{findPrimersTabState.get('results')!.models.length}</strong> primer(s) were found.
                  </span>
                )}
              </p>
              {findPrimersTabState.get('lowerTmResults') && (
                <p>
                  <img src={staticURL('/static/img/warning.png')} /> Matches with low melting temperatures
                  were found but not shown.
                </p>
              )}
              {findPrimersTabState.get('higherTmResults') && (
                <p>
                  <img src={staticURL('/static/img/warning.png')} /> Matches with high melting temperatures
                  were found but not shown.
                </p>
              )}
              <FindPrimersResults
                highlightMatches={findPrimersTabState.get('highlightMatches')}
                findType={findType}
                sequence={sequence}
                results={findPrimersTabState.get('results')!.models}
                // FindPrimersOptionsView listens for this event to re-render.
                onSelectPrimer={() => findPrimersTabState.trigger('selectChange')}
                onClickEdit={(primer: SequencePrimer) => {
                  EditPrimerTabState.edit(primer.clone(), sequence);
                }}
              />
              {findPrimersTabState.get('results')!.models.length === 0 && (
                <div className="well">No binding locations were found.</div>
              )}
              {findType === FindPrimersOptions.BASES && (
                <>
                  <hr />
                  <SaveBasesAsOligoForm
                    color={findPrimersTabState.get('color')}
                    setColor={(color) => findPrimersTabState.set('color', color)}
                    createOligoAndSavePrimers={createOligoAndSavePrimers}
                    folderId={findPrimersTabState.get('selectedSaveLibrary')}
                    setFolderId={(folderId) => findPrimersTabState.set('selectedSaveLibrary', folderId)}
                    name={findPrimersTabState.get('primerName')}
                    setName={(name) => findPrimersTabState.set('primerName', name)}
                    saveDisabled={
                      !sequence.allowFeatureEdits() || findPrimersTabState.getNewPrimers().length === 0
                    }
                  />
                </>
              )}
            </>
          )}
        </>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  positionInput: {
    width: '50px',
    textAlign: 'right',
  },
  positionText: {
    width: '66%',
    display: 'inline-block',
  },
  useSelectionButton: {
    width: '33%',
    display: 'inline-block',
  },
  baseCoercionWarning: {
    color: GRAY_DARKER,
    ...FONT_PROXIMA_11,
  },
});
